import { Button } from "@bleu.builders/ui";
import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import React, { useCallback, useEffect, useState } from "react";

import { formatDate } from "#/lib/formatDate";

const styleFont = {
  font: "Verdana",
};

const styleHeadingTitle = {
  size: 32,
  color: "#222222",
  break: 1,
  ...styleFont,
};

const styleHeadingInfo = {
  break: 2,
  size: 18,
  color: "#4d4d4d",
  ...styleFont,
};

const styleHeadingDocumentName = {
  break: 2,
  size: 25,
  ...styleFont,
};

const styleTitle = {
  size: 28,
  ...styleFont,
};

const styleSubtitle = {
  bold: true,
  break: 1,
  size: 22,
  color: "#4D4D4D",
  ...styleFont,
};

const styleResume = {
  break: 1,
  size: 22,
  color: "#4D4D4D",
  ...styleFont,
};

export function DiagnosisReportDOCX({ table, diagnosis, groupLabel }) {
  const [linkDocumentWord, setLinkDocumentWord] = useState<HTMLAnchorElement>(
    {} as HTMLAnchorElement,
  );
  const [loading, setLoading] = useState(true);

  function extractParagraphs(htmlText) {
    const parser = new DOMParser();
    let doc = parser.parseFromString(htmlText, "text/html");
    let paragraphs = doc.querySelectorAll("p");

    // If no paragraph elements, wrap the whole text inside a p element
    if (paragraphs.length === 0) {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = `<p>${htmlText}</p>`;
      doc = parser.parseFromString(wrapper.innerHTML, "text/html");
      paragraphs = doc.querySelectorAll("p");
    }
    // extract the text from each paragraph and return as an array
    return Array.from(paragraphs).map((p) => p.textContent.trim());
  }

  const buildDocument = useCallback(() => {
    const sections = [];

    sections.push({
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [
            new TextRun({
              text: "Oportunidades Tributárias",
              ...styleHeadingTitle,
            }),
            new TextRun({
              text: diagnosis.title,
              ...styleHeadingDocumentName,
            }),
            new TextRun({
              text: `Teses agrupadas por: ${groupLabel}`,
              ...styleHeadingInfo,
            }),
            new TextRun({
              text: `Última atualização em ${formatDate(diagnosis.updated_at)}`,
              ...styleHeadingInfo,
            }),
          ],
        }),
      ],
    });

    for (const row of table.getRowModel().rows) {
      // remove the row if it is not grouped or if it is the "Teses Arquivadas" group
      if (!row.getIsGrouped() || row.groupingValue === "Teses Arquivadas") {
        continue;
      }
      sections.push({
        children: [
          new Paragraph({ children: [new TextRun({ break: 25 })] }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: row.groupingValue, ...styleTitle })],
            heading: HeadingLevel.HEADING_1,
            shading: { color: "#000000" },
          }),
        ],
      });
      row.getLeafRows().forEach((thesis) => {
        sections.push({
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: thesis.original.title, ...styleTitle }),
              ],
              heading: HeadingLevel.HEADING_2,
            }),
            new Paragraph({
              children: [new TextRun({ text: "Objetivo", ...styleSubtitle })],
            }),
            new Paragraph({
              indent: { end: 20 },
              children: [
                new TextRun({
                  text: thesis.original.objective,
                  ...styleResume,
                }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: "Resumo da Tese", ...styleSubtitle }),
              ],
            }),
            new Paragraph({
              indent: { end: 20 },
              children: [
                ...extractParagraphs(thesis.original.summary).map(
                  (resume: string) => {
                    return new TextRun({ text: resume, ...styleResume });
                  },
                ),
              ],
            }),
          ],
        });
      });
    }
    return sections;
  }, [table]);

  const addDocumentToDownload = useCallback(() => {
    const sections = buildDocument();
    const doc = new Document({ sections });
    Packer.toBlob(doc).then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const linkDocumentWordEl = document.createElement("a");
      linkDocumentWordEl.href = url;
      linkDocumentWordEl.setAttribute("download", `${diagnosis.title}.docx`);
      document.body.appendChild(linkDocumentWordEl);
      setLinkDocumentWord(linkDocumentWordEl);
    });
  }, [diagnosis.title]);

  useEffect(() => {
    addDocumentToDownload();
    setLoading(false);
  }, [table.getRowModel()]);

  function onDownload() {
    linkDocumentWord.click();
  }

  const DownloadDisabled =
    loading || typeof linkDocumentWord.click !== "function";

  return (
    <Button onClick={() => onDownload()} disabled={DownloadDisabled}>
      {DownloadDisabled ? "Gerando docx..." : "DOCX"}
    </Button>
  );
}
